import { SITE_DESCRIPTION, SITE_NAME, SITE_URL } from "./config";
import { isOpenLink } from "../config/stores";
import {
    breadcrumbSchema,
    faqPageSchema,
    mobileApplicationSchema,
    webPageSchema,
} from "./schemas";

function pageSchemas(title, path, description, extra = []) {
    const url = `${SITE_URL}${path}`;
    return [
        webPageSchema({ title, description, url }),
        breadcrumbSchema([
            { name: "Home", url: SITE_URL },
            { name: title, url },
        ]),
        ...extra,
    ];
}

export const PAGE_SEO = {
    "/": {
        title: `${SITE_NAME} | Save, Track, Pay & Earn Rewards in One Money App`,
        description: SITE_DESCRIPTION,
        keywords:
            "Accruewise app, money app India, save money app, spend tracker, SMS expense tracker, bill payment, recharge, prepaid RuPay card, rewards",
        schemas: [
            mobileApplicationSchema(),
            webPageSchema({
                title: `${SITE_NAME} | Save, Track, Pay & Earn Rewards in One Money App`,
                description: SITE_DESCRIPTION,
                url: SITE_URL,
            }),
        ],
    },
    "/features": {
        title: "Accruewise Features | Goals, Spend Tracker, Bills, UPI, RuPay Card & Rewards",
        description:
            "Every Accruewise feature: savings goals, automatic spend tracking from bank SMS, insights and budgets, bills and recharges, UPI and IMPS transfers, NCMC top-up, a prepaid RuPay card, rewards and an AI assistant.",
        keywords:
            "Accruewise features, savings goals app, SMS expense tracker, bill payment app India, IMPS transfer, NCMC top-up, prepaid RuPay card, rewards app",
        schemas: pageSchemas(
            "Features",
            "/features",
            "Every Accruewise feature: savings goals, automatic spend tracking from bank SMS, insights and budgets, bills and recharges, UPI and IMPS transfers, NCMC top-up, a prepaid RuPay card, rewards and an AI assistant."
        ),
    },
    "/waitlist": {
        title: "Join the Accruewise Waitlist | iPhone Launch & Early Access",
        description:
            "Join the Accruewise waitlist: be first to know when the iPhone app launches and get early access to new features. Android users can start today.",
        keywords: "Accruewise waitlist, Accruewise iPhone, Accruewise iOS app, early access",
        schemas: pageSchemas(
            "Waitlist",
            "/waitlist",
            "Join the Accruewise waitlist: be first to know when the iPhone app launches and get early access to new features. Android users can start today."
        ),
    },
    "/download": {
        title: "Download Accruewise | Google Play and the App Store",
        description: "Get the Accruewise app. This link opens Google Play on Android and the App Store on iPhone.",
        robots: "noindex, follow",
        schemas: [],
    },
    "/card": {
        title: "Accruewise Prepaid RuPay Card | Spend What You Saved",
        description:
            "Get the Accruewise prepaid RuPay card: a virtual card on your Accruewise Wallet for online and in-store payments, with block, PIN reset and usage controls in the app.",
        keywords:
            "Accruewise card, prepaid RuPay card, virtual card India, Accruewise Wallet, digital payments",
        schemas: pageSchemas(
            "Virtual Card",
            "/card",
            "Get the Accruewise prepaid RuPay card: a virtual card on your Accruewise Wallet for online and in-store payments, with block, PIN reset and usage controls in the app.",
            [
                {
                    "@context": "https://schema.org",
                    "@type": "FinancialProduct",
                    name: "Accruewise Virtual Card",
                    provider: { "@type": "Organization", name: SITE_NAME },
                    description:
                        "A prepaid RuPay card on the Accruewise Wallet for online and in-store payments, with card controls inside the Accruewise app.",
                },
            ]
        ),
    },
    "/marketplace": {
        title: "Accruewise Marketplace | Save for the Things You Want",
        description:
            "Pick a product in the Accruewise Marketplace and plan for its price with a goal and a weekly amount, then pay with your wallet and prepaid RuPay card.",
        keywords:
            "Accruewise marketplace, product savings goal, save to buy, goal-based saving India",
        schemas: pageSchemas(
            "Marketplace",
            "/marketplace",
            "Pick a product in the Accruewise Marketplace and plan for its price with a goal and a weekly amount, then pay with your wallet and prepaid RuPay card."
        ),
    },
    "/ai-agents": {
        title: "Accruewise Assistant | Ask Your Money a Question, in Hindi Too",
        description:
            "Meet the Accruewise Assistant: ask about your spends, create a savings goal in one sentence, and pay bills and recharges you approve with your MPIN or fingerprint, in English or Hindi.",
        keywords:
            "Accruewise Assistant, AI money assistant, voice savings goal, Hindi money app, AI personal finance India",
        schemas: pageSchemas(
            "AI Agents",
            "/ai-agents",
            "Meet the Accruewise Assistant: ask about your spends, create a savings goal in one sentence, and pay bills and recharges you approve with your MPIN or fingerprint, in English or Hindi."
        ),
    },
    "/about-us": {
        title: "About Accruewise | Making Money Management Empowering",
        description:
            "Learn about Accruewise, an India-first fintech company building one app for saving toward goals, tracking spends, paying bills and spending with a prepaid RuPay card.",
        keywords: "about Accruewise, fintech company India, Accruewise team",
        schemas: pageSchemas(
            "About Us",
            "/about-us",
            "Learn about Accruewise, an India-first fintech company building one app for saving toward goals, tracking spends, paying bills and spending with a prepaid RuPay card."
        ),
    },
    "/faqs": {
        title: "Accruewise FAQs | Answers About Saving, Payments & the App",
        description:
            "Answers about Accruewise: the iPhone app, automatic spend tracking, bills and recharges, adding and sending money, the RuPay card, NCMC, security, Hindi and data privacy.",
        keywords: "Accruewise FAQ, Accruewise help, Accruewise support questions",
        schemas: [faqPageSchema(), ...pageSchemas(
            "FAQs",
            "/faqs",
            "Answers about Accruewise: the iPhone app, automatic spend tracking, bills and recharges, adding and sending money, the RuPay card, NCMC, security, Hindi and data privacy."
        )],
    },
    "/blog": {
        title: `Accruewise Blog | Saving, Budgeting & Money Tips`,
        description:
            "Practical guides on saving, budgeting, spending smarter, and building better money habits — from the Accruewise team.",
        keywords: "Accruewise blog, savings tips, budgeting guide, personal finance India",
        schemas: pageSchemas(
            "Blog",
            "/blog",
            "Practical guides on saving, budgeting, spending smarter, and building better money habits."
        ),
    },
    "/contact-us": {
        title: "Contact Accruewise | Customer Support & Help",
        description:
            "Contact Accruewise for product support, account help, and business inquiries. Email care@accruewise.in or use the contact page.",
        keywords: "contact Accruewise, Accruewise support, customer care",
        schemas: pageSchemas(
            "Contact Us",
            "/contact-us",
            "Contact Accruewise for product support, account help, and business inquiries.",
            [
                {
                    "@context": "https://schema.org",
                    "@type": "ContactPage",
                    name: "Contact Accruewise",
                    url: `${SITE_URL}/contact-us`,
                },
            ]
        ),
    },
    "/privacy-policy": {
        title: "Accruewise Privacy Policy",
        description:
            "Read the Accruewise privacy policy to understand how personal data is collected, used, stored, and protected on the website and mobile app.",
        schemas: pageSchemas(
            "Privacy Policy",
            "/privacy-policy",
            "Read the Accruewise privacy policy to understand how personal data is collected, used, stored, and protected."
        ),
    },
    "/terms-and-conditions": {
        title: "Accruewise Terms of Service",
        description:
            "Review Accruewise terms of service for website and app usage, account responsibilities, communications, and legal conditions.",
        schemas: pageSchemas(
            "Terms of Service",
            "/terms-and-conditions",
            "Review Accruewise terms of service for website and app usage, account responsibilities, and legal conditions."
        ),
    },
    "/grievance-redressal-policy": {
        title: "Accruewise Grievance Redressal Policy",
        description:
            "How to raise a complaint with Accruewise and how to escalate it if it is not resolved.",
        schemas: pageSchemas(
            "Grievance Redressal",
            "/grievance-redressal-policy",
            "How to raise a complaint with Accruewise and how to escalate it if it is not resolved."
        ),
    },
    "/refund-and-cancellation": {
        title: "Accruewise Refund & Cancellation Policy",
        description:
            "Read Accruewise refund and cancellation policy details for services, transactions, and customer grievance support.",
        schemas: pageSchemas(
            "Refund and Cancellation",
            "/refund-and-cancellation",
            "Read Accruewise refund and cancellation policy details for services, transactions, and customer grievance support."
        ),
    },
};

export const NOT_FOUND_SEO = {
    title: `Page Not Found | ${SITE_NAME}`,
    description: "The page you are looking for could not be found on Accruewise.",
    robots: "noindex, follow",
    schemas: [],
};

// /open/<route> links behind the buttons in WhatsApp messages: one person's link, never indexed
// (robots.txt also disallows /open/).
export const OPEN_LINK_SEO = {
    title: `Open in the app | ${SITE_NAME}`,
    description: "This link opens a page in the Accruewise app. Don't have it yet? Get the app free.",
    robots: "noindex, nofollow",
    schemas: [],
};

export function getPageSeo(pathname) {
    // /app and /get are short aliases of the download link.
    if (pathname === "/app" || pathname === "/get") return PAGE_SEO["/download"];
    if (isOpenLink(pathname)) return OPEN_LINK_SEO;
    return PAGE_SEO[pathname] || NOT_FOUND_SEO;
}
